import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { CSSProperties, Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetBenefitVerifiedStatusQuery,
  useGetBenefitWorklistQuery,
} from "store/queries/benefitComparison";
import styled from "styled-components";
import {
  EngineDisagreement,
  IComparisonChecksRequest,
  ISourceAgreementCell,
  IWorklistFilters,
  IWorklistRow,
  WorklistFlaggedFilter,
  WorklistState,
} from "types/BenefitComparison/BenefitComparison";

import { BenefitComparisonCluster } from "./BenefitComparisonCluster";
import {
  accuracySampleText,
  accuracyText,
  ACCURACY_SPLIT_LABELS,
  ENGINE_DISAGREEMENT_FILTERS,
  ENGINE_DISAGREEMENT_PLACEHOLDER,
  AGREEMENT_HEADING,
  AGREEMENT_TOOLTIP,
  agreementPercent,
  agreementRows,
  agreementSampleText,
  agreementText,
  COMPARISON_BENEFIT_LABELS,
  COMPARISON_BENEFITS,
  COVERAGE_INTRO,
  ENGINE_ACCURACY_TOOLTIP,
  otherSourcesText,
  OTHER_SOURCES_TOOLTIP,
  rangeText,
  REGRESSED_CARD_TEXT,
  ACCURACY_BENEFIT_ORDER,
  SCORECARD_BENEFIT_LABELS,
  SCORECARD_BENEFIT_ORDER,
  verdictsText,
  WORKLIST_INTRO,
  worklistEmptyMessage,
  WORKLIST_STATE_LABELS,
} from "./benefitComparisonWording";
import {
  DismissClusterDialog,
  WorklistDismissalButtons,
  WorklistDismissalStatus,
} from "./BenefitWorklistDismissal";
import {
  FlagClusterDialog,
  WorklistFlagButton,
  WorklistFlaggedTag,
} from "./BenefitWorklistFlag";
import { BenefitWorklistFreshness } from "./BenefitWorklistFreshness";
import { BenefitWorklistTrend } from "./BenefitWorklistTrend";
import { EngineDisagreesWith } from "./EngineDisagreesWith";
import { READ_FROM_LABELS, relativeTime } from "./remitAccuracyWording";

const PARAM = {
  account: "account",
  payer: "payer",
  benefit: "benefit",
  disagreesWith: "disagreesWith",
  regressed: "regressed",
  page: "page",
  /** "1" = also list hidden clusters ("Hide cluster"). Absent (the default) hides them. */
  showMarkedCorrect: "showMarkedCorrect",
  /** "only" = flagged clusters only, "none" = unflagged only ("Flag for review"). Absent = all. */
  flagged: "flagged",
  clusterAccount: "clusterAccount",
  clusterPayer: "clusterPayer",
  clusterPlan: "clusterPlan",
  clusterNoPlan: "clusterNoPlan",
  clusterBenefit: "clusterBenefit",
  clusterReadFrom: "clusterReadFrom",
  clusterState: "clusterState",
  clusterDisagreesWith: "clusterDisagreesWith",
} as const;

const CLUSTER_PARAMS = [
  PARAM.clusterAccount,
  PARAM.clusterPayer,
  PARAM.clusterPlan,
  PARAM.clusterNoPlan,
  PARAM.clusterBenefit,
  PARAM.clusterReadFrom,
  PARAM.clusterState,
  PARAM.clusterDisagreesWith,
];

/**
 * The URL a worklist row opens its cluster detail with. 🚨 ONE FUNCTION WRITES IT AND READS IT BACK:
 * `openCluster` puts these params in the URL, and `isViewedCluster` finds the row the detail view is
 * showing by comparing the same params — so the detail view's "Hide cluster" acts on that row, and
 * sends `clusterKeyOf(row)`, the very key the row's own button sends.
 */
const clusterParamsOf = (
  row: IWorklistRow
): Record<string, string | undefined> => ({
  [PARAM.clusterAccount]: row.accountId,
  [PARAM.clusterPayer]: row.payerId === null ? "none" : String(row.payerId),
  [PARAM.clusterPlan]: row.planName ?? undefined,
  [PARAM.clusterNoPlan]: row.planName === null ? "1" : undefined,
  [PARAM.clusterBenefit]: row.benefit ?? undefined,
  [PARAM.clusterReadFrom]: row.readFrom,
  [PARAM.clusterState]: row.state,
  [PARAM.clusterDisagreesWith]: row.engineDisagreesWith.join(",") || undefined,
});

/** The params that NAME a cluster. State and the source chips describe it and may have moved since. */
const CLUSTER_IDENTITY_PARAMS = [
  PARAM.clusterAccount,
  PARAM.clusterPayer,
  PARAM.clusterPlan,
  PARAM.clusterNoPlan,
  PARAM.clusterBenefit,
  PARAM.clusterReadFrom,
];

const isViewedCluster = (row: IWorklistRow, params: URLSearchParams) => {
  const own = clusterParamsOf(row);
  return CLUSTER_IDENTITY_PARAMS.every(
    (key) => (own[key] ?? null) === params.get(key)
  );
};

/**
 * The filters that change which rows are listed. Changing one sends the table back to its first
 * page — page 4 of a list that the new filter cut to one page would show nothing.
 */
const FILTER_PARAMS: string[] = [
  PARAM.account,
  PARAM.payer,
  PARAM.benefit,
  PARAM.disagreesWith,
  PARAM.regressed,
  PARAM.showMarkedCorrect,
  PARAM.flagged,
];

/** The "Flagged" dropdown. `null` = all clusters, written to the URL as no param at all. */
const FLAGGED_OPTIONS: { label: string; value: WorklistFlaggedFilter }[] = [
  { label: "Flagged only", value: "only" },
  { label: "Not flagged", value: "none" },
];

/**
 * 🚨 A FLAGGED ROW GETS A SUBTLE RED BORDER AROUND THE WHOLE ROW — drawn as INSET BOX-SHADOWS ON EACH
 * CELL, not as borders and not on the `<tr>`.
 *
 * PrimeReact's DataTable uses `border-collapse: collapse`. Under it, `outline` / `box-shadow` on a
 * `<tr>` are unreliable, and real `border-top` on a cell loses to the grey bottom border of the row
 * above: two 1px solid borders on two cells resolve to the one ABOVE (CSS 2.1 §17.6.2.1), so the
 * flagged row's top edge would draw grey. An inset shadow is painted inside each cell, outside the
 * border-conflict rules, so all four edges are red whatever the neighbours are — and it sits over a
 * REGRESSED row's `bg-red-50` as well.
 */
const FLAG_BORDER = "var(--red-300, #fca5a5)";
const WorklistTable = styled.div`
  .p-datatable .p-datatable-tbody > tr.benefitWorklist-flagged > td {
    box-shadow: inset 0 1px 0 ${FLAG_BORDER}, inset 0 -1px 0 ${FLAG_BORDER};
  }
  .p-datatable
    .p-datatable-tbody
    > tr.benefitWorklist-flagged
    > td:first-child {
    box-shadow: inset 0 1px 0 ${FLAG_BORDER}, inset 0 -1px 0 ${FLAG_BORDER},
      inset 1px 0 0 ${FLAG_BORDER};
  }
  .p-datatable .p-datatable-tbody > tr.benefitWorklist-flagged > td:last-child {
    box-shadow: inset 0 1px 0 ${FLAG_BORDER}, inset 0 -1px 0 ${FLAG_BORDER},
      inset -1px 0 0 ${FLAG_BORDER};
  }
`;

/** The `?payer=` value for a payer option. Clusters whose payer could not be resolved are `none`. */
const payerParam = (payerId: number | null) =>
  payerId === null ? "none" : String(payerId);

/** 🚨 Client-side paging over the ranked list the API already returned. */
const WORKLIST_PAGE_SIZE = 50;

const oneOf = <T extends string>(value: string | null, allowed: T[]) =>
  value !== null && allowed.includes(value as T) ? (value as T) : undefined;

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

/**
 * 🚨 `data-pr-tooltip` DOES NOTHING WITHOUT A MOUNTED `<Tooltip>` TARGETING IT. Both info icons on
 * this screen carried the attribute and neither showed anything on hover. One `<Tooltip>` for the
 * whole tab, matched by this class — the pattern the rest of the SPA already uses.
 */
const TOOLTIP_CLASS = "benefitWorklistTip";

/** A card in the scorecard row. Before the first nightly run every figure is a dash, with no copy. */
function Card({
  label,
  children,
  tone,
  testId,
  tooltip,
}: {
  label: string;
  children: React.ReactNode;
  tone?: "danger";
  testId: string;
  tooltip?: string;
}) {
  return (
    <div
      className={`border-1 border-round p-3 h-full flex flex-column gap-2 ${
        tone === "danger" ? "border-red-300 bg-red-50" : "surface-border"
      }`}
      data-testid={testId}
    >
      <span className="align-self-center text-xs font-bold uppercase text-color-secondary">
        {label}
        {tooltip && (
          <i
            className={`pi pi-info-circle ml-2 text-xs ${TOOLTIP_CLASS}`}
            data-pr-tooltip={tooltip}
            data-testid={`${testId}.tooltip`}
          />
        )}
      </span>
      {children}
    </div>
  );
}

/** A count's share, rounded — with "<1%" rather than "0%", because 31 of 6,911 is not nothing. */
const sharePercentText = (count: number, total: number) => {
  if (total === 0) return "—";
  const share = (100 * count) / total;
  if (count > 0 && share < 1) return "<1%";
  return `${Math.round(share)}%`;
};

/**
 * The scorecard's benefit percentages: one row per benefit, one column per half of the split.
 *
 * 🚨 A LABELLED COLUMN, NOT A SECOND FIGURE BESIDE THE FIRST. The two numbers on a row measure
 * different things, so they have to be told apart at a glance and from the same place on every row —
 * a heading above each column does that, and "95.0% / 78.5%" on one line does not.
 *
 * 🚨 ONE GRID AT EVERY WIDTH, no breakpoint. The card is `xl:col-3`, so the narrow case is the
 * normal one and a layout that only works wide would be the layout nobody sees. The two number
 * columns are `auto` — they take exactly what the widest fraction ("1,195 / 1,216" at `text-xs`)
 * needs and give the rest to the label, which is allowed to wrap. Abbreviating the labels ("Phys
 * Copay") was the alternative and reads worse for the few pixels it saves.
 *
 * Numbers are right-aligned inside their column, so the percentages land on two vertical lines and
 * can be read DOWN against one another rather than wherever their label happened to end.
 */
const AccuracyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.4rem 0.75rem;
  align-items: center;
`;

/**
 * One half of one benefit: the percentage over the sample it was measured on.
 *
 * 🚨 "—%" AND "no cases", NEVER "0%", for a half with nothing in it. Co-insurance has no not-charged
 * checks at all, and a "0%" there would read as the engine failing rather than as nothing to measure
 * — the same distinction the headline already makes before the first nightly run.
 */
function AccuracyHalf({
  matches,
  verified,
  testId,
}: {
  matches: number;
  verified: number;
  testId: string;
}) {
  return (
    <span
      className="flex flex-column align-items-end line-height-2"
      data-testid={testId}
    >
      <strong
        className={`white-space-nowrap ${
          verified ? "" : "text-color-secondary"
        }`}
      >
        {verified ? accuracyText(matches, verified) : "—%"}
      </strong>
      <span className="text-xs text-color-secondary white-space-nowrap">
        {accuracySampleText(matches, verified)}
      </span>
    </span>
  );
}

/**
 * The bar rows inside one coverage group: label, track, count.
 *
 * 🚨 The track is CAPPED rather than filling the column. A bar stretched across half the
 * screen to show 8% reads as a measurement error; bounded, the three of them read as one comparison.
 */
const COVERAGE_ROWS: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "10.5rem minmax(4rem, 22rem) 6.5rem",
  gap: "0.75rem 1rem",
  alignItems: "center",
};

/** One bar in the coverage strip. 🚨 One denominator for every bar, so they are comparable. */
function CoverageBar({
  label,
  count,
  total,
  accent,
}: {
  label: string;
  count: number;
  total: number;
  accent?: boolean;
}) {
  const share = total === 0 ? 0 : (100 * count) / total;
  const shareText = sharePercentText(count, total);
  return (
    <>
      <span>{label}</span>
      <div
        className="border-round surface-200 overflow-hidden"
        style={{ height: "6px" }}
      >
        <div
          className={accent ? "bg-orange-600" : "bg-blue-700"}
          style={{
            height: "6px",
            width: `${Math.max(share, count > 0 ? 0.5 : 0)}%`,
          }}
        />
      </div>
      <span className="text-right">
        <strong>{count.toLocaleString()}</strong> <Muted>{shareText}</Muted>
      </span>
    </>
  );
}

/**
 * One benefit's agreement card: the five buckets as bars, over the compared-check count.
 *
 * 🚨 THE DENOMINATOR IS THAT BENEFIT'S OWN `compared`, not a shared one, because AI coverage differs
 * per benefit. So the five bars within ONE card sum to 100% and are comparable to each other; two
 * cards' bars are NOT comparable as counts, which is why every card carries its own sample size.
 */
function AgreementCard({ cell }: { cell: ISourceAgreementCell }) {
  return (
    <div
      className="border-1 surface-border border-round p-3 flex flex-column gap-2"
      data-testid={`benefitWorklist.agreement.${cell.benefit}`}
    >
      <div className="flex flex-column">
        <span className="font-bold">
          {SCORECARD_BENEFIT_LABELS[cell.benefit]}
        </span>
        <span className="text-xs text-color-secondary">
          {agreementSampleText(cell.compared)}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "8.5rem minmax(2rem, 1fr) 3.75rem",
          gap: "0.4rem 0.6rem",
          alignItems: "center",
        }}
      >
        {agreementRows(cell).map(({ key, label, count }) => {
          const share = agreementPercent(count, cell.compared) ?? 0;
          return (
            <Fragment key={key}>
              <span className="text-sm">{label}</span>
              <div
                className="border-round surface-200 overflow-hidden"
                style={{ height: "6px" }}
              >
                <div
                  // 🚨 "None agree" is the one worth spotting, so it is the one that is not blue.
                  className={key === "none" ? "bg-orange-600" : "bg-blue-700"}
                  style={{
                    height: "6px",
                    width: `${Math.max(share, count > 0 ? 0.5 : 0)}%`,
                  }}
                />
              </div>
              <span className="text-sm text-right">
                {cell.compared === 0 ? (
                  <Muted>—%</Muted>
                ) : (
                  agreementText(count, cell.compared)
                )}
              </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

const nameBody = (row: IWorklistRow) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <Muted>
      {row.state === "NOT_CONFIGURED"
        ? "all plans"
        : row.planName ?? "No plan name"}
    </Muted>
  </div>
);

const engineSaysBody = (row: IWorklistRow) => {
  if (row.benefit === null) return <Muted>—</Muted>;
  const text = rangeText(row.engineMin, row.engineMax, row.benefit);
  return text || <Muted>no value</Muted>;
};

/**
 * Every source that has a value: what it reads, who says it, and whether the engine differs from it.
 *
 * 🚨 THE ≠ IS THE POINT OF THE COLUMN NOW. Listing legacy, the AI and a remit together only helps if
 * you can see which of them the engine is actually at odds with — a source can appear here carrying
 * the engine's own value (see `otherSourcesText`). Differing sources are marked and set in body text;
 * agreeing ones are greyed, so the cell reads as evidence rather than as a list.
 */
const otherSourcesBody = (row: IWorklistRow) => {
  const lines = otherSourcesText(row);
  if (lines.length === 0) return <Muted>—</Muted>;
  return (
    <div className="flex flex-column gap-1">
      {lines.map((line) => (
        <span
          key={line.key}
          className="white-space-nowrap"
          data-testid={`benefitWorklist.otherSource.${line.key}`}
        >
          <span
            className={line.differs ? "font-semibold" : "text-color-secondary"}
          >
            {line.differs ? "≠ " : ""}
            {line.value}
          </span>{" "}
          <Muted>{line.source}</Muted>
        </span>
      ))}
    </div>
  );
};

/**
 * 🚨 "Nothing" is the ABSENCE of a read, so it is greyed like "no value" in Engine says rather than
 * set in body text as though it named somewhere the engine actually looked.
 */
const readFromBody = (row: IWorklistRow) => {
  if (!row.readFrom) return <Muted>—</Muted>;
  const label = READ_FROM_LABELS[row.readFrom] ?? row.readFrom;
  return row.readFrom === "NONE" ? (
    <Muted>{label}</Muted>
  ) : (
    <span className="white-space-nowrap">{label}</span>
  );
};

/** 🚨 The same chips the response panel uses, from the one component both screens render. */
const disagreesWithBody = (row: IWorklistRow) => (
  <EngineDisagreesWith
    state={row.state}
    sources={row.engineDisagreesWith}
    testId="benefitWorklist.engineDisagreesWith"
  />
);

/**
 * CLE-2820 — THE ENGINE WORKLIST. One screen: a scorecard that answers "is the engine getting
 * better" in thirty seconds, a coverage strip that says why a number is thin, and one ranked list of
 * clusters that answers "what do I fix next". Internal, SuperAdmin only.
 *
 * It replaced three views behind a `View` SelectButton (Compare / Review queue / Verified) that were
 * three lenses on the same clusters. The lens is now the State COLUMN, and the ranking is one pure
 * function on the API (`rankWorklist`).
 *
 * 🚨 TWO THINGS DELIBERATELY ABSENT, both decided by the screen's owner:
 *
 * 1. THERE IS NO DATE FILTER. Every number here is all-time. A cluster wrong since August outranks
 *    one that appeared last week, which is the order worth fixing in — and the old dropdown governed
 *    the cluster counts while the verified figures ignored it, with nothing saying which half.
 *
 * 2. THERE IS NO "ENGINE AT TIME OF CHECK" ANYWHERE. Only today's configuration can be fixed, so
 *    only today's configuration is shown. The `QUOTED` picks still exist in the database and nothing
 *    in this UI reads them; they would come back as a per-check detail if support ever needs to
 *    answer "what were we quoting in August".
 *
 * 🚨 AND NO KEYBOARD SHORTCUTS. Selecting a check is done by clicking its row, and that is the only
 * mechanism — no digit keys, no J/K, no global keydown handler.
 */
export function BenefitComparisonTab() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const accountId = params.get(PARAM.account) ?? undefined;
  const payerId = params.get(PARAM.payer) ?? undefined;
  const benefit = oneOf(params.get(PARAM.benefit), COMPARISON_BENEFITS);
  /**
   * 🚨 THE AXIS IS "WHICH SOURCE", NOT "WHICH STATE". The worklist exists to compare the engine
   * against each other selector, and a cluster has ONE state but can disagree with several sources —
   * so filtering on the state could never answer "show me where the engine and legacy differ".
   */
  const disagreesWith = oneOf(params.get(PARAM.disagreesWith), [
    ...ENGINE_DISAGREEMENT_FILTERS.map((option) => option.value),
  ]);
  const regressedOnly =
    disagreesWith === undefined && params.get(PARAM.regressed) === "1";

  const update = (changes: Record<string, string | undefined>, push = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (Object.keys(changes).some((key) => FILTER_PARAMS.includes(key)))
          next.delete(PARAM.page);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === undefined) next.delete(key);
          else next.set(key, value);
        });
        return next;
      },
      { replace: !push }
    );

  /**
   * 🚨 BENEFIT IS NOT SENT. Account and payer change what the WHOLE PAGE reports and go to the API;
   * Benefit now filters the worklist table alone, in the browser, beside State. The coverage strip,
   * the Engine accuracy card, the agreement cards and the configuration gaps deliberately keep
   * showing every benefit while the table is narrowed to one.
   */
  const filters: IWorklistFilters = useMemo(
    () => ({ accountId, payerId }),
    [accountId, payerId]
  );
  /**
   * 🚨 ONLY THE WORKLIST READ GETS `includeDismissed`. The verified scorecard shares `filters` and must
   * not move: hiding a cluster changes which rows are listed, never a number. The
   * freshness banner takes `worklistFilters` so it keeps sharing the worklist's one cache entry.
   */
  const hideMarkedCorrect = params.get(PARAM.showMarkedCorrect) !== "1";
  /**
   * 🚨 `flagged` GOES TO THE API TOO, for the same reason as `includeDismissed`: the API caps the list
   * at `WORKLIST_LIMIT`, so filtering in the browser could lose a flagged cluster ranked below the cap.
   * And like it, it reaches `worklistFilters` only — never `filters`, which the scorecard shares.
   */
  const flagged = oneOf<WorklistFlaggedFilter>(params.get(PARAM.flagged), [
    "only",
    "none",
  ]);
  const worklistFilters: IWorklistFilters = useMemo(
    () => ({
      ...filters,
      ...(hideMarkedCorrect ? {} : { includeDismissed: true }),
      ...(flagged ? { flagged } : {}),
    }),
    [filters, hideMarkedCorrect, flagged]
  );
  const [dismissing, setDismissing] = useState<IWorklistRow | null>(null);
  const [flagging, setFlagging] = useState<IWorklistRow | null>(null);
  const { data, currentData, isLoading, isError } =
    useGetBenefitWorklistQuery(worklistFilters);
  const worklist = data?.data;

  /**
   * 🚨 A PAYER THE WORKLIST NO LONGER OFFERS GOES BACK TO "ALL PAYERS". The options are the payers on
   * the worklist under the account filter, so switching account can leave the selected payer with no
   * rows at all — an empty table that looks like "nothing to do". Read off `currentData`, never
   * `data`: `data` still holds the PREVIOUS account's answer while the new one loads, and would reset
   * a payer the new account does have. A payer whose clusters are all hidden is still offered (at 0),
   * so the hidden-clusters checkbox never trips this.
   */
  const offeredPayers = currentData?.data.options.payers;
  useEffect(() => {
    if (payerId === undefined || offeredPayers === undefined) return;
    if (offeredPayers.some((payer) => payerParam(payer.payerId) === payerId))
      return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(PARAM.payer);
        next.delete(PARAM.page);
        return next;
      },
      { replace: true }
    );
  }, [payerId, offeredPayers, setParams]);
  const verified = useGetBenefitVerifiedStatusQuery(filters).data?.data;

  const clusterAccount = params.get(PARAM.clusterAccount);
  const clusterBenefit = oneOf(
    params.get(PARAM.clusterBenefit),
    COMPARISON_BENEFITS
  );
  const clusterReadFrom = params.get(PARAM.clusterReadFrom);
  const inCluster = Boolean(
    clusterAccount && clusterBenefit && clusterReadFrom
  );
  /**
   * The worklist row of the cluster being viewed, for its hide and flag controls. Found in the list
   * already loaded; when it is not there, a filter may be what left it out — "Leave out hidden
   * clusters", or the "Flagged" dropdown (flag a cluster from here under "Not flagged" and it leaves
   * the primary list at once) — so the list is read once more WITH hidden clusters and WITHOUT the
   * flag filter (`filters` never carries it). Only then, so the list view never pays for it; and not
   * when that read would be the primary one again. Not on either list (it no longer disagrees, or a
   * stale deep link): no controls, as no row has them.
   */
  const listedRow = inCluster
    ? worklist?.rows.find((row) => isViewedCluster(row, params))
    : undefined;
  const lookupIsPrimary = !hideMarkedCorrect && flagged === undefined;
  const hiddenLookup = useGetBenefitWorklistQuery(
    { ...filters, includeDismissed: true },
    {
      skip:
        !inCluster || lookupIsPrimary || !worklist || listedRow !== undefined,
    }
  );
  const viewedRow =
    listedRow ??
    (inCluster && !lookupIsPrimary
      ? hiddenLookup.data?.data.rows.find((row) => isViewedCluster(row, params))
      : undefined);

  const backToList = () =>
    update(
      Object.fromEntries(CLUSTER_PARAMS.map((key) => [key, undefined])),
      true
    );

  /**
   * 🚨 ONE DIALOG FOR BOTH VIEWS, AT THE SAME PLACE IN BOTH TREES. Hiding from the cluster detail
   * goes back to the list, and the "Hidden ✓" burst lives in this dialog's state — so the dialog must
   * survive the switch, which it does only because both returns render it second in a fragment.
   */
  const dismissDialog = (
    <DismissClusterDialog
      row={dismissing}
      onHide={() => setDismissing(null)}
      onDismissed={inCluster ? backToList : undefined}
    />
  );
  /** The flag confirm. Flagging keeps the row on the list, so it never navigates anywhere. */
  const flagDialog = (
    <FlagClusterDialog row={flagging} onHide={() => setFlagging(null)} />
  );

  /**
   * The cluster's own actions, ONE ROW: `leading` (Analyze / Configure on the list, the "Flagged for
   * review" tag on the detail view), then the flag icon, then the hide icon(s) — with the Hidden tag
   * and its date beneath. A configuration gap gets no flag and no hide: it is not a cluster.
   */
  const clusterActions = (row: IWorklistRow, leading: React.ReactNode) => (
    <div className="flex flex-column align-items-start gap-1">
      <div className="flex align-items-center gap-1">
        {leading}
        <WorklistFlagButton
          row={row}
          tooltipClass={TOOLTIP_CLASS}
          onFlag={setFlagging}
        />
        <WorklistDismissalButtons
          row={row}
          tooltipClass={TOOLTIP_CLASS}
          onDismiss={setDismissing}
        />
      </div>
      <WorklistDismissalStatus row={row} tooltipClass={TOOLTIP_CLASS} />
    </div>
  );

  if (clusterAccount && clusterBenefit && clusterReadFrom) {
    const request: IComparisonChecksRequest = {
      accountId: clusterAccount,
      payerId: params.get(PARAM.clusterPayer) ?? "none",
      benefit: clusterBenefit,
      readFrom: clusterReadFrom,
      // 🚨 Today's configuration, always. There is no engine-side parameter on this screen.
      engine: "REPLAY",
      ...(params.get(PARAM.clusterNoPlan) === "1"
        ? { planNameMissing: true }
        : { planName: params.get(PARAM.clusterPlan) ?? undefined }),
    };
    return (
      <>
        <Surface data-testid="benefitComparisonTab">
          <Tooltip target={`.${TOOLTIP_CLASS}`} position="top" />
          <BenefitComparisonCluster
            request={request}
            state={oneOf(
              params.get(PARAM.clusterState),
              Object.keys(WORKLIST_STATE_LABELS) as WorklistState[]
            )}
            /*
             * 🚨 CARRIED FROM THE ROW, not re-derived here. The cluster detail reports counts by
             * PATTERN (`onlyEngineDiffers` and friends) and cannot tell a remit from a human verdict,
             * so deriving the chips on this screen would quietly disagree with the row that opened it.
             */
            disagreesWith={(params.get(PARAM.clusterDisagreesWith) ?? "")
              .split(",")
              .filter((source): source is EngineDisagreement =>
                ENGINE_DISAGREEMENT_FILTERS.some(
                  (option) => option.value === source
                )
              )}
            onBack={backToList}
            actions={
              viewedRow &&
              clusterActions(
                viewedRow,
                <WorklistFlaggedTag
                  row={viewedRow}
                  tooltipClass={TOOLTIP_CLASS}
                />
              )
            }
          />
        </Surface>
        {dismissDialog}
        {flagDialog}
      </>
    );
  }

  const openCluster = (row: IWorklistRow) => {
    // An unconfigured account has no cluster to open: the answer is to write a configuration.
    if (row.state === "NOT_CONFIGURED" || row.benefit === null) {
      navigate(`/hospitals/${row.accountId}?tab=benefits`);
      return;
    }
    update(clusterParamsOf(row), true);
  };

  /** 🚨 Analyze / Configure stays a readable text button: it is the primary action. The rest are icons. */
  const actionBody = (row: IWorklistRow) =>
    clusterActions(
      row,
      <Button
        type="button"
        label={row.state === "NOT_CONFIGURED" ? "Configure" : "Analyze"}
        className="p-button-text p-button-sm"
        data-testid="benefitWorklist.open"
        onClick={() => openCluster(row)}
      />
    );

  const coverage = worklist?.coverage;
  const totals = verified?.totals;
  /**
   * The table's own two filters, both applied HERE rather than by the API.
   *
   * 🚨 IT KEEPS EVERYTHING ABOVE THE TABLE HONEST. The Regressed count, the coverage bars and the
   * agreement cards are all-time figures; narrowing the table must not move them, and filtering
   * server-side would.
   *
   * 🚨 A `NOT_CONFIGURED` ROW SURVIVES A BENEFIT FILTER, because its `benefit` is null meaning ALL of
   * them — the account has no configuration for the selected benefit either, so the row is as true
   * under the filter as without it. Dropping it would hide a real gap from the person narrowing down
   * to the benefit it affects.
   */
  const rows = (worklist?.rows ?? []).filter(
    (row) =>
      // 🚨 `includes`, not equality: a row names EVERY source the engine differs from, so a cluster
      // where legacy and the AI both differ is returned by either filter.
      (!disagreesWith || row.engineDisagreesWith.includes(disagreesWith)) &&
      (!regressedOnly || row.state === "REGRESSED") &&
      (!benefit || row.benefit === benefit || row.benefit === null)
  );

  /**
   * 🚨 THE PAGE IS IN THE URL, beside the filters, so opening a cluster and coming back — by the
   * cluster's own back button or the browser's — lands on the page you left. Left to the DataTable
   * it was local state, lost every time the cluster replaced the table. Clamped to the last page,
   * because a hand-edited or stale `?page=` past the end would otherwise show an empty table.
   */
  const requestedPage = Math.max(
    1,
    Math.floor(Number(params.get(PARAM.page))) || 1
  );
  const lastPage = Math.max(1, Math.ceil(rows.length / WORKLIST_PAGE_SIZE));
  const first =
    (Math.min(requestedPage, rows.length ? lastPage : requestedPage) - 1) *
    WORKLIST_PAGE_SIZE;

  return (
    <>
      <Surface data-testid="benefitComparisonTab">
        <Tooltip target={`.${TOOLTIP_CLASS}`} position="top" />
        {/*
         * 🚨 Account and payer. Nothing else up here — no date, no chips, no view switch.
         *
         * Benefit used to stand here too, and used to narrow the whole page. It moved down beside the
         * table and now filters only that table — so these two are exactly the filters that change what
         * the CARDS report, and the two below are exactly the ones that change which ROWS are listed.
         */}
        <div className="flex flex-wrap align-items-end gap-3 mb-4">
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.account">Account</label>
            <Dropdown
              inputId="benefitWorklist.account"
              value={accountId ?? null}
              options={worklist?.options.accounts ?? []}
              optionLabel="accountName"
              optionValue="accountId"
              placeholder="All accounts"
              showClear
              filter
              className="w-16rem"
              onChange={(e) =>
                update({ [PARAM.account]: e.value ?? undefined })
              }
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.payer">Payer</label>
            <Dropdown
              inputId="benefitWorklist.payer"
              value={payerId ?? null}
              /*
               * The payers ON THIS WORKLIST under the account filter, not every payer we hold a check
               * for, each with its cluster count — the same rows the table lists before the two
               * in-browser filters below narrow it. "No payer" is last.
               */
              options={(worklist?.options.payers ?? []).map((payer) => ({
                label: `${payer.payerName} (${payer.clusters})`,
                value: payerParam(payer.payerId),
              }))}
              placeholder="All payers"
              showClear
              filter
              className="w-16rem"
              onChange={(e) => update({ [PARAM.payer]: e.value ?? undefined })}
            />
          </div>
        </div>

        {isError && (
          <Message
            severity="error"
            className="mb-3"
            text="The engine worklist could not be loaded."
          />
        )}

        {/* 🚨 `worklistFilters`: the banner shares the worklist's cache entry — see its own comment. */}
        <BenefitWorklistFreshness filters={worklistFilters} />

        <div className="grid mb-2">
          <div className="col-12 md:col-6 xl:col-3">
            <Card
              label="Regressed"
              testId="benefitWorklist.regressed"
              tone={
                worklist && worklist.regressed.count > 0 ? "danger" : undefined
              }
            >
              <span
                className={`align-self-center text-4xl font-bold ${
                  worklist?.regressed.count
                    ? "text-red-700"
                    : "text-color-secondary"
                }`}
              >
                {worklist ? worklist.regressed.count.toLocaleString() : "—"}
              </span>
              <Muted>
                <span className="block text-center">{REGRESSED_CARD_TEXT}</span>
                {worklist?.regressed.count ? (
                  <>
                    {" "}
                    Broke under config {worklist.regressed.configVersion ?? "—"}
                    {worklist.regressed.at
                      ? `, ${relativeTime(worklist.regressed.at)}`
                      : ""}
                    .{" "}
                    <Button
                      type="button"
                      className="p-button-link p-0 font-semibold"
                      label={
                        regressedOnly
                          ? "Show everything"
                          : `Show the ${worklist.regressed.count} →`
                      }
                      data-testid="benefitWorklist.showRegressed"
                      onClick={() =>
                        // 🚨 Its own parameter again. "Regressed" is not a SOURCE — it is "a remit or
                        // verdict that used to match and no longer does" — so it cannot be one of the
                        // four options without meaning something different from the others.
                        update({
                          [PARAM.regressed]: regressedOnly ? undefined : "1",
                          [PARAM.disagreesWith]: undefined,
                        })
                      }
                    />
                  </>
                ) : null}
              </Muted>
            </Card>
          </div>

          <div className="col-12 md:col-6 xl:col-3">
            <Card
              label="Engine accuracy"
              testId="benefitWorklist.accuracy"
              tooltip={ENGINE_ACCURACY_TOOLTIP}
            >
              <span
                className={`align-self-center text-4xl font-bold ${
                  totals?.verified ? "" : "text-color-secondary"
                }`}
                data-testid="benefitWorklist.accuracy.headline"
              >
                {totals?.verified
                  ? accuracyText(totals.matches, totals.verified)
                  : "—%"}
              </span>
              {/*
               * 🚨 EVERY BENEFIT TWICE, SPLIT ON WHAT THE PAYER CHARGED. A single percentage per
               * benefit conflated the checks where the patient owed nothing — the majority, and the
               * ones the engine gets right by saying so — with the checks that cost a patient money.
               * On prod 2026-09-22 physician copay read 26.7% while the engine matched NONE of the
               * 111 checks where a physician copay was actually charged; that fact was invisible.
               *
               * 🚨 THE SPLIT IS ON THE VERIFIED VALUE, decided on the API. Splitting on the engine's
               * own value would sort the checks by the thing being measured.
               *
               * The headline above is unchanged: it is still the whole verified set.
               */}
              <AccuracyGrid className="text-sm">
                <span />
                <span
                  className="text-xs font-bold uppercase text-color-secondary text-right"
                  data-testid="benefitWorklist.accuracy.chargedHeading"
                >
                  {ACCURACY_SPLIT_LABELS.charged}
                </span>
                <span
                  className="text-xs font-bold uppercase text-color-secondary text-right"
                  data-testid="benefitWorklist.accuracy.zeroHeading"
                >
                  {ACCURACY_SPLIT_LABELS.zero}
                </span>
                {ACCURACY_BENEFIT_ORDER.map((each) => {
                  const cell = verified?.benefits.find(
                    (row) => row.benefit === each
                  );
                  return (
                    <Fragment key={each}>
                      <span data-testid={`benefitWorklist.accuracy.${each}`}>
                        {SCORECARD_BENEFIT_LABELS[each]}
                      </span>
                      <AccuracyHalf
                        matches={cell?.chargedMatches ?? 0}
                        verified={cell?.chargedVerified ?? 0}
                        testId={`benefitWorklist.accuracy.${each}.charged`}
                      />
                      <AccuracyHalf
                        matches={cell?.zeroMatches ?? 0}
                        verified={cell?.zeroVerified ?? 0}
                        testId={`benefitWorklist.accuracy.${each}.zero`}
                      />
                    </Fragment>
                  );
                })}
              </AccuracyGrid>
            </Card>
          </div>

          <div className="col-12 md:col-6 xl:col-3">
            <Card
              label="Since last config change"
              testId="benefitWorklist.sinceChange"
            >
              {worklist?.sinceLastConfigChange.changedAt ? (
                <>
                  <div className="flex-1 flex align-items-center justify-content-center gap-5">
                    <span className="flex flex-column align-items-center">
                      <span className="text-4xl font-bold text-green-700">
                        +{worklist.sinceLastConfigChange.fixed}
                      </span>
                      <Muted>fixed</Muted>
                    </span>
                    <span className="flex flex-column align-items-center">
                      <span className="text-4xl font-bold text-red-700">
                        −{worklist.sinceLastConfigChange.regressed}
                      </span>
                      <Muted>regressed</Muted>
                    </span>
                  </div>
                  <Muted>
                    <span className="block text-center">
                      {relativeTime(worklist.sinceLastConfigChange.changedAt)}
                    </span>
                  </Muted>
                </>
              ) : (
                <span className="flex-1 flex align-items-center justify-content-center text-4xl font-bold text-color-secondary">
                  —
                </span>
              )}
            </Card>
          </div>

          <div className="col-12 md:col-6 xl:col-3">
            <Card label="Trend" testId="benefitWorklist.trend">
              <BenefitWorklistTrend />
            </Card>
          </div>
        </div>

        {/*
         * 🚨 NOT PrimeFlex `grid`/`col` in here. `.grid` carries a −0.5rem margin, so the inner row of
         * bars hung half a rem to the left of its own group heading while the other group (a plain
         * grid) sat flush — the two groups were indented differently on the same strip. Plain CSS grid
         * both times: one row of groups with a real gap between them, one grid of bars inside each,
         * every left edge on the same line.
         */}
        <div className="border-1 surface-border border-round p-3 mb-4">
          <div className="flex flex-wrap align-items-baseline gap-2 mb-3">
            <span className="text-xs font-bold uppercase text-color-secondary">
              Coverage
            </span>
            <Muted>{COVERAGE_INTRO(coverage?.checks ?? 0)}</Muted>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(26rem, 1fr))",
              gap: "1.5rem 4rem",
            }}
          >
            <div>
              <span className="font-bold block mb-3">
                Benefit selector algorithms
              </span>
              <div
                style={COVERAGE_ROWS}
                data-testid="benefitWorklist.coverage.selectors"
              >
                <CoverageBar
                  label="Legacy"
                  count={coverage?.legacy ?? 0}
                  total={coverage?.checks ?? 0}
                />
                <CoverageBar
                  label="Engine"
                  count={coverage?.engine ?? 0}
                  total={coverage?.checks ?? 0}
                />
                <CoverageBar
                  label="AI"
                  count={coverage?.ai ?? 0}
                  total={coverage?.checks ?? 0}
                />
              </div>
            </div>
            <div>
              <span className="font-bold block mb-3">
                Sources of truth to compare them against
              </span>
              <div
                style={COVERAGE_ROWS}
                data-testid="benefitWorklist.coverage.truth"
              >
                <CoverageBar
                  label="Remits"
                  count={coverage?.remits ?? 0}
                  total={coverage?.checks ?? 0}
                  accent
                />
                <CoverageBar
                  label="Human input"
                  count={coverage?.verdicts ?? 0}
                  total={coverage?.checks ?? 0}
                  accent
                />
              </div>
            </div>
          </div>
        </div>

        {/*
         * CLE-2820 follow-up — HOW OFTEN THE THREE SELECTORS AGREE, one card per benefit.
         *
         * 🚨 THE ONLY NUMBER DEDUCTIBLE AND OUT-OF-POCKET HAVE. The Engine accuracy card above shows
         * "—%" for both and always will: a remit says what the patient owed on one claim, never what
         * they had REMAINING at check time, so neither benefit has a truth source and
         * `BenefitVerificationStatus` holds zero rows for them. Agreement needs no truth at all.
         *
         * 🚨 AND IT IS NOT ACCURACY. Three selectors agreeing on a wrong value agree just as loudly.
         * The caption says so, because a row of high percentages beside a card headed ENGINE ACCURACY
         * would otherwise read as a second accuracy score.
         */}
        <div className="border-1 surface-border border-round p-3 mb-4">
          <div className="flex flex-wrap align-items-baseline gap-2 mb-3">
            <span className="text-xs font-bold uppercase text-color-secondary">
              {AGREEMENT_HEADING}
            </span>
            <i
              className={`pi pi-info-circle text-color-secondary ${TOOLTIP_CLASS}`}
              data-pr-tooltip={AGREEMENT_TOOLTIP}
              data-testid="benefitWorklist.agreement.tooltip"
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(17rem, 1fr))",
              gap: "1rem",
            }}
            data-testid="benefitWorklist.agreement"
          >
            {SCORECARD_BENEFIT_ORDER.map((benefit) => {
              const cell = worklist?.agreement.find(
                (each) => each.benefit === benefit
              );
              return (
                <AgreementCard
                  key={benefit}
                  cell={
                    cell ?? {
                      benefit,
                      compared: 0,
                      allThree: 0,
                      legacyAndEngine: 0,
                      legacyAndAi: 0,
                      engineAndAi: 0,
                      none: 0,
                    }
                  }
                />
              );
            })}
          </div>
        </div>

        {/*
         * 🚨 No "engine last run …" here. It was never in the design and it answered a question
         * nobody asks of a worklist; the cluster screen states it beside the re-run button, which is
         * the one place it changes what someone does next.
         */}
        <div className="flex flex-wrap align-items-baseline gap-3 mb-3">
          <h3 className="m-0">Worklist</h3>
          <Muted>{WORKLIST_INTRO}</Muted>
        </div>

        {/*
         * 🚨 THE TWO FILTERS THAT ANSWER "WHICH ROWS OF THIS TABLE", next to the table.
         *
         * Both narrow THIS TABLE AND NOTHING ELSE, in the browser. Benefit used to be a page filter and
         * went to the API; it no longer does, so the coverage strip, the accuracy card and the agreement
         * cards go on reporting every benefit while the table shows one. That is deliberate — those are
         * all-time figures and a table filter must not move them.
         */}
        <div className="flex flex-wrap align-items-end gap-3 mb-3">
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.disagreesWith">
              Engine disagrees with
            </label>
            <Dropdown
              inputId="benefitWorklist.disagreesWith"
              value={disagreesWith ?? null}
              options={ENGINE_DISAGREEMENT_FILTERS.map((each) => ({
                label: each.label,
                value: each.value,
              }))}
              placeholder={ENGINE_DISAGREEMENT_PLACEHOLDER}
              showClear
              /*
               * 🚨 SIZED FOR THE LONGEST LABEL, which is "Human input disagrees" — with
               * "Legacy + AI disagree" and "Every source differs" a character behind it. At the 14rem
               * the other filters use, the clear and chevron icons leave under 150px for text and the
               * selected state reads "Legacy + AI dis…", which is the one thing this control exists to
               * tell you. It is on its own row, so the extra width costs the other filters nothing.
               */
              className="w-20rem"
              onChange={(e) =>
                update({
                  [PARAM.disagreesWith]: e.value ?? undefined,
                  [PARAM.regressed]: undefined,
                })
              }
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.benefit">Benefit</label>
            <Dropdown
              inputId="benefitWorklist.benefit"
              value={benefit ?? null}
              options={COMPARISON_BENEFITS.map((each) => ({
                label: COMPARISON_BENEFIT_LABELS[each],
                value: each,
              }))}
              placeholder="All benefits"
              showClear
              className="w-14rem"
              onChange={(e) =>
                update({ [PARAM.benefit]: e.value ?? undefined })
              }
            />
          </div>
          {/*
           * "Flag for review". 🚨 GOES TO THE API, like the checkbox below and for the same reason: a
           * flagged cluster ranked below the API's row cap must still be listed under "Flagged only".
           * It changes only the table. The count beside the label is every flagged cluster under the
           * account and payer — it does not move with this dropdown.
           */}
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.flagged">
              Flagged
              {(worklist?.flaggedCount ?? 0) > 0 && (
                <Muted> ({worklist?.flaggedCount} flagged)</Muted>
              )}
            </label>
            <Dropdown
              inputId="benefitWorklist.flagged"
              value={flagged ?? null}
              options={FLAGGED_OPTIONS}
              placeholder="All clusters"
              showClear
              className="w-14rem"
              data-testid="benefitWorklist.flaggedFilter"
              onChange={(e) =>
                update({ [PARAM.flagged]: e.value ?? undefined })
              }
            />
          </div>
          {/*
           * 🚨 GOES TO THE API, unlike the two filters beside it, because hidden rows must not take
           * places under the API's row cap. It still changes only the table: the cards above are
           * summed from every cluster, marked or not.
           */}
          <div className="flex align-items-center gap-2 pb-2">
            <Checkbox
              inputId="benefitWorklist.hideMarkedCorrect"
              checked={hideMarkedCorrect}
              onChange={(e) =>
                update({
                  [PARAM.showMarkedCorrect]: e.checked ? undefined : "1",
                })
              }
              data-testid="benefitWorklist.hideMarkedCorrect"
            />
            <label htmlFor="benefitWorklist.hideMarkedCorrect">
              Leave out hidden clusters
              {hideMarkedCorrect && (worklist?.dismissedHidden ?? 0) > 0 && (
                <Muted> ({worklist?.dismissedHidden} hidden)</Muted>
              )}
            </label>
          </div>
        </div>

        {/*
         * 🚨 PAGINATED AT 50, client side: the API ranks and returns the whole list, and this is one
         * ranked queue meant to be worked from the top — sorting or filtering a page of it would
         * answer a different question. The API's own cap is `WORKLIST_LIMIT`.
         */}
        <WorklistTable>
          <DataTable
            value={rows}
            paginator
            rows={WORKLIST_PAGE_SIZE}
            first={first}
            onPage={(e) => {
              const page = Math.floor(e.first / WORKLIST_PAGE_SIZE) + 1;
              update({ [PARAM.page]: page > 1 ? String(page) : undefined });
            }}
            loading={isLoading}
            // Both may apply: a REGRESSED row that is also flagged is pink with a red border.
            rowClassName={(row: IWorklistRow) =>
              [
                row.state === "REGRESSED" ? "bg-red-50" : "",
                row.flag ? "benefitWorklist-flagged" : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
            emptyMessage={worklistEmptyMessage(
              Boolean(disagreesWith) ||
                Boolean(benefit) ||
                regressedOnly ||
                Boolean(flagged)
            )}
            data-testid="benefitWorklist.rows"
          >
            <Column
              header="#"
              body={(_row, options) => options.rowIndex + 1}
              style={{ width: "3rem" }}
            />
            {/*
             * 🚨 EVERY SHORT COLUMN HAS A FIXED WIDTH AND THE ACCOUNT COLUMN HAS NONE, so the slack on a
             * wide screen goes to the one column that can use it. Sized on content it took nearly half
             * the table and the short ones wrapped for want of a few pixels; pinned to 26rem it stayed
             * squeezed while "Human verdicts" sat on inches of white space for the word "—". The plan
             * name is the only thing here allowed to wrap, being the only thing long enough to need to.
             */}
            <Column
              header="Account · Payer · Plan"
              body={nameBody}
              style={{ minWidth: "18rem" }}
            />
            <Column
              header="Benefit"
              style={{ width: "9rem" }}
              body={(row: IWorklistRow) =>
                row.benefit ? COMPARISON_BENEFIT_LABELS[row.benefit] : "All"
              }
              headerClassName="white-space-nowrap"
              bodyClassName="white-space-nowrap"
            />
            {/*
             * 🚨 HEADED LIKE THE FILTER IT ANSWERS. It used to say "State", which named the tag that is
             * no longer the cell's primary content — and the filter above the table says "Engine
             * disagrees with", so the two now read as the same axis.
             */}
            <Column
              header="Engine disagrees with"
              style={{ width: "12rem" }}
              body={disagreesWithBody}
              headerClassName="white-space-nowrap"
            />
            {/* 🚨 The column that makes the rank order legible. Do not drop it. */}
            <Column
              header="Checks"
              style={{ width: "6rem" }}
              body={(row: IWorklistRow) =>
                row.total === 0
                  ? row.affected.toLocaleString()
                  : `${row.affected} / ${row.total}`
              }
              headerClassName="white-space-nowrap"
              bodyClassName="white-space-nowrap"
            />
            <Column
              header="Engine says"
              style={{ width: "8rem" }}
              body={engineSaysBody}
              headerClassName="white-space-nowrap"
              bodyClassName="white-space-nowrap"
            />
            <Column
              header={
                <span className="white-space-nowrap">
                  Other sources say
                  <i
                    className={`pi pi-info-circle ml-2 text-xs font-normal text-color-secondary ${TOOLTIP_CLASS}`}
                    data-pr-tooltip={OTHER_SOURCES_TOOLTIP}
                    data-testid="benefitWorklist.otherSources.tooltip"
                  />
                </span>
              }
              style={{ width: "14rem" }}
              body={otherSourcesBody}
            />
            <Column
              header="Engine read from"
              style={{ width: "11rem" }}
              body={readFromBody}
              headerClassName="white-space-nowrap"
            />
            <Column
              header="Human input"
              style={{ width: "8rem" }}
              body={(row: IWorklistRow) => verdictsText(row)}
              headerClassName="white-space-nowrap"
              bodyClassName="white-space-nowrap"
            />
            {/*
             * Fixed like every other short column: "Analyze" plus up to three icons (flag, hide, undo
             * on a stale hide) on one line, with the Hidden tag and its date beneath.
             */}
            <Column header="" body={actionBody} style={{ width: "13rem" }} />
          </DataTable>
        </WorklistTable>
      </Surface>
      {dismissDialog}
      {flagDialog}
    </>
  );
}
